import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Loader } from '@googlemaps/js-api-loader'
import axios from 'axios'
import { DatePicker } from 'antd'
import type { Dayjs } from 'dayjs'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { appApiUrls } from 'src/shared/url-map.js'
import { MAP_LOCATION_SAMPLE_LIMIT, MAP_PATH_GROUPS } from 'src/shared/constants.js'
import type { MapPathGroup } from 'src/shared/constants.js'
import type { APIResponse, MapLocationResponse, MapSearchLocationData } from 'src/shared/common-types.js'

interface LocationMapProps {
  className?: string
}

// 地图标记图标常量
const MAP_MARKER_ICONS = {
  AMAP: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEyIDJDOC4xMyAyIDUgNS4xMyA1IDlDNSAxNC4yNSAxMiAyMiAxMiAyMkMxMiAyMiAxOSAxNC4yNSAxOSA5QzE5IDUuMTMgMTUuODcgMiAxMiAyWk0xMiAxMS41QzEwLjYyIDExLjUgOS41IDEwLjM4IDkuNSA5QzkuNSA3LjYyIDEwLjYyIDYuNSAxMiA2LjVDMTMuMzggNi41IDE0LjUgNy42MiAxNC41IDlDMTQuNSAxMC4zOCAxMy4zOCAxMS41IDEyIDExLjVaIiBmaWxsPSIjRkY0NDQ0Ii8+Cjwvc3ZnPgo=',
  GMAP: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEyIDJDOC4xMyAyIDUgNS4xMyA1IDlDNSAxNC4yNSAxMiAyMiAxMiAyMkMxMiAyMiAxOSAxNC4yNSAxOSA5QzE5IDUuMTMgMTUuODcgMiAxMiAyWk0xMiAxMS41QzEwLjYyIDExLjUgOS41IDEwLjM4IDkuNSA5QzkuNSA3LjYyIDEwLjYyIDYuNSAxMiA2LjVDMTMuMzggNi41IDE0LjUgNy42MiAxNC41IDlDMTQuNSAxMC4zOCAxMy4zOCAxMS41IDEyIDExLjVaIiBmaWxsPSIjNDI4NUY0Ii8+Cjwvc3ZnPgo=',
}

const PROVIDER_COLORS = { amap: '#FF4444', gmap: '#4285F4' }

const REGEO_PATHS: readonly string[] = MAP_PATH_GROUPS.regeo
const SEARCH_PATHS: readonly string[] = MAP_PATH_GROUPS.search

/** 拿不到国家码的记录（请求失败、Google 侧没返回）统一归到这个桶 */
const UNKNOWN_COUNTRY = 'unknown'

// 国名用浏览器自带的 Intl 翻，省得自己维护一张 ISO 码到中文的表
const countryDisplayNames = (() => {
  try {
    return new Intl.DisplayNames(['zh-CN'], { type: 'region' })
  } catch {
    return null
  }
})()

/** ISO 两位码转国旗 emoji：A-Z 依次映射到 Regional Indicator 码点 */
const countryFlag = (code: string) =>
  String.fromCodePoint(...[...code.toUpperCase()].map((char) => 0x1f1e6 + char.charCodeAt(0) - 65))

// Intl 给的是「中国香港特别行政区」这种全称，chip 里太长
const SHORT_REGION_NAMES: Record<string, string> = { HK: '香港', MO: '澳门' }

const countryLabel = (code: string) => {
  if (code === UNKNOWN_COUNTRY) return '未知'
  if (!/^[A-Za-z]{2}$/.test(code)) return code
  const name = SHORT_REGION_NAMES[code.toUpperCase()] ?? countryDisplayNames?.of(code.toUpperCase())
  return `${countryFlag(code)} ${name && name !== code.toUpperCase() ? name : code.toUpperCase()}`
}

const shortToken = (token: string) => (token.length > 8 ? `${token.slice(0, 4)}..${token.slice(-3)}` : token)

const countryOf = (location: MapSearchLocationData) => location.isoCountryCode || UNKNOWN_COUNTRY
const deviceOf = (location: MapSearchLocationData) => location.userToken || 'unknown'

/** 设备 chips 默认只露这么多，一个国家上百台设备时手机上不至于刷好几屏 */
const DEVICE_CHIP_LIMIT = 20

/**
 * 离已有的点经纬度都不到 0.001°（约 100 米）就算同一处。同一台设备在家里后台唤醒一天能请求几百次，
 * GPS 每次漂几十米，按精确坐标分组照样叠成一摞，看不出到底几次。
 * 不直接按保留 3 位小数分组：一个地方正好压在格线上会被劈成两个点，小的那个又藏在大的底下
 */
const SAME_POINT_DEGREES = 0.001

/** 地图上的一个点：落在同一处的成功请求合成一个，数字就是次数 */
type LocationPoint = {
  /** 取第一条请求的 id，选中态靠它认人 */
  key: string
  lat: number
  lng: number
  /** 沿用接口的顺序，按时间倒序 */
  requests: MapSearchLocationData[]
  latest: MapSearchLocationData
  earliest: MapSearchLocationData
}

const groupByPoint = (locations: MapSearchLocationData[]) => {
  const points: LocationPoint[] = []
  // 按 SAME_POINT_DEGREES 分格建索引，只在相邻 9 格里找，样本上万条也不用两两比
  const grid = new Map<string, LocationPoint[]>()
  const cellOf = (degrees: number) => Math.floor(degrees / SAME_POINT_DEGREES)

  locations.forEach((location) => {
    const { requestLat: lat, requestLon: lng } = location
    // 失败的请求没有落点结果，不上图
    if (typeof lat !== 'number' || typeof lng !== 'number' || !location.isSuccess) return

    const row = cellOf(lat)
    const col = cellOf(lng)
    let point: LocationPoint | undefined
    for (let dr = -1; dr <= 1 && !point; dr++) {
      for (let dc = -1; dc <= 1 && !point; dc++) {
        point = grid
          .get(`${row + dr},${col + dc}`)
          ?.find((p) => Math.abs(p.lat - lat) <= SAME_POINT_DEGREES && Math.abs(p.lng - lng) <= SAME_POINT_DEGREES)
      }
    }

    if (point) {
      point.requests.push(location)
      point.earliest = location
      return
    }
    const created = { key: location.id, lat, lng, requests: [location], latest: location, earliest: location }
    points.push(created)
    const cell = `${row},${col}`
    grid.set(cell, [...(grid.get(cell) ?? []), created])
  })
  return points
}

/** 只有一次请求的地方还是原来的大头针；叠了多次的画成带数字的圆，选中时放大一圈 */
const markerIcon = (maps: any, point: LocationPoint, selected: boolean) => {
  const provider = point.latest.provider
  const count = point.requests.length
  if (count === 1) {
    const size = selected ? 36 : 24
    return {
      url: provider === 'amap' ? MAP_MARKER_ICONS.AMAP : MAP_MARKER_ICONS.GMAP,
      scaledSize: new maps.Size(size, size),
    }
  }
  const radius = 9 + String(count).length * 2
  return {
    path: maps.SymbolPath.CIRCLE,
    scale: selected ? radius + 4 : radius,
    fillColor: provider === 'amap' ? PROVIDER_COLORS.amap : PROVIDER_COLORS.gmap,
    fillOpacity: 0.9,
    strokeColor: '#ffffff',
    strokeWeight: selected ? 3 : 1.5,
  }
}

const formatTime = (createdAt?: string) =>
  createdAt
    ? new Date(createdAt).toLocaleString('zh-CN', {
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'N/A'

type FilterChipProps = {
  active: boolean
  /** 在另一个维度的筛选下没有记录，灰掉但还能点 */
  muted?: boolean
  title?: string
  onClick: () => void
  children: React.ReactNode
}

const FilterChip = ({ active, muted = false, title, onClick, children }: FilterChipProps) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 transition-colors ${
      active
        ? 'border-blue-500 bg-blue-100 dark:border-blue-400 dark:bg-blue-900/40'
        : 'border-gray-200 bg-gray-50 hover:border-blue-300 dark:border-gray-600 dark:bg-slate-700'
    } ${muted && !active ? 'opacity-40' : ''}`}
  >
    {children}
  </button>
)

const TIME_RANGE_OPTIONS = [
  { value: 'today', label: '今天' },
  { value: 'yesterday', label: '昨天' },
  { value: '7days', label: '最近7天' },
  { value: 'thisWeek', label: '本周' },
  { value: '30days', label: '最近30天' },
]

/** 标记详情里要展示的字段，顺序即展示顺序；空值直接不出现 */
const detailRows = (location: MapSearchLocationData, mapSubTab: 'regeo' | 'search') => {
  const actual = location.actualLocation
  const rows: { label: string; value: string }[] = [
    {
      label: '请求坐标',
      value:
        typeof location.requestLat === 'number' && typeof location.requestLon === 'number'
          ? `${location.requestLat.toFixed(6)}, ${location.requestLon.toFixed(6)}`
          : 'N/A',
    },
  ]

  if (actual && typeof actual.lat === 'number' && typeof actual.lon === 'number') {
    rows.push(
      { label: '实际位置', value: `${actual.lat.toFixed(6)}, ${actual.lon.toFixed(6)}` },
      { label: '距离', value: `${actual.distance ?? 'N/A'} 米` },
      { label: '类型', value: actual.type || 'N/A' },
    )
  }

  if (mapSubTab === 'search') {
    if (location.keyword) rows.push({ label: '搜索关键字', value: location.keyword })
    if (location.radius) rows.push({ label: '搜索半径', value: `${location.radius} 米` })
    if (location.resultCount !== undefined) rows.push({ label: '结果数量', value: String(location.resultCount) })
  }

  rows.push(
    { label: '提供商', value: location.provider === 'amap' ? '高德地图' : 'Google地图' },
    { label: '语言', value: location.language || 'N/A' },
    { label: '时间', value: location.createdAt ? new Date(location.createdAt).toLocaleString('zh-CN') : 'N/A' },
  )

  return rows
}

export default function LocationMap({ className = '' }: LocationMapProps) {
  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<any>(null)
  const markersRef = useRef<any[]>([])
  const circlesRef = useRef<any[]>([])
  const googleMapsRef = useRef<any>(null)
  const detailRef = useRef<HTMLDivElement>(null)
  const [selectedTimeRange, setSelectedTimeRange] = useState('today')
  const [locations, setLocations] = useState<MapSearchLocationData[]>([])
  // 总数和设备数来自服务端聚合，不受地图采样上限影响
  const [userCount, setUserCount] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [truncated, setTruncated] = useState(false)
  // 默认只看正式版；testBuildCount 是这个窗口里模拟器 / 调试包 / TestFlight 的请求数，开关关着时就是被排除掉的条数
  const [includeTestBuilds, setIncludeTestBuilds] = useState(false)
  const [testBuildCount, setTestBuildCount] = useState(0)
  const [loading, setLoading] = useState(false)
  // 下面的请求列表只看失败的那部分；失败记录取自当前筛选结果，不单独拉数据
  const [showFailedOnly, setShowFailedOnly] = useState(false)
  const [mapLoaded, setMapLoaded] = useState(false)
  // 国家和设备各自单选，两者是 AND：改其中一个不会清掉另一个
  const [selectedUser, setSelectedUser] = useState<string | null>(null)
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null)
  const [showAllDevices, setShowAllDevices] = useState(false)
  // 点标记后在地图下方展开详情，不用 InfoWindow：气泡在 iPhone 上撑不开，内容一多只能靠手指在气泡里滚
  const [selectedPoint, setSelectedPoint] = useState<LocationPoint | null>(null)
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const subTabParam = searchParams.get('subTab')
  const [mapSubTab, setMapSubTab] = useState<'regeo' | 'search'>(subTabParam === 'search' ? 'search' : 'regeo')

  const handleSubTabChange = (subTab: 'regeo' | 'search') => {
    setMapSubTab(subTab)
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (subTab === 'regeo') {
        next.delete('subTab')
      } else {
        next.set('subTab', subTab)
      }
      return next
    })
  }

  // 按 path 类型过滤数据
  const regeoLocations = useMemo(() => locations.filter((loc) => REGEO_PATHS.includes(loc.path)), [locations])
  const searchLocations = useMemo(() => locations.filter((loc) => SEARCH_PATHS.includes(loc.path)), [locations])
  const activeLocations = mapSubTab === 'regeo' ? regeoLocations : searchLocations

  // 国家和设备筛选只作用在 chips 下面的地图和请求列表上，是「看细节」的维度，
  // 上面那条统计栏仍然是全量口径，不跟着筛选走。
  // 每一维单独过滤一份，给另一维的 chips 算数字用：选了设备，国家 chips 显示这台设备在各国的请求数，反过来也一样
  const countryFilteredLocations = useMemo(
    () => (selectedCountry ? activeLocations.filter((loc) => countryOf(loc) === selectedCountry) : activeLocations),
    [activeLocations, selectedCountry],
  )
  const deviceFilteredLocations = useMemo(
    () => (selectedUser ? activeLocations.filter((loc) => deviceOf(loc) === selectedUser) : activeLocations),
    [activeLocations, selectedUser],
  )

  // 国家 AND 设备的结果，地图和下面的请求列表共用这一份。
  // 这里和 mapPoints 都必须 memo：地图重绘挂在它们的引用上，每渲染一次就换个新数组的话，
  // 点标记引起的重渲染会把所有标记重建一遍，顺带 fitBounds 把用户的缩放和平移也弹回去
  const filteredLocations = useMemo(
    () =>
      selectedUser
        ? countryFilteredLocations.filter((loc) => deviceOf(loc) === selectedUser)
        : countryFilteredLocations,
    [countryFilteredLocations, selectedUser],
  )
  const failedLocations = useMemo(() => filteredLocations.filter((loc) => !loc.isSuccess), [filteredLocations])
  const mapPoints = useMemo(() => groupByPoint(filteredLocations), [filteredLocations])

  // 初始化Google Maps
  useEffect(() => {
    const initMap = async () => {
      if (!mapRef.current) return

      // 确保容器已经在 DOM 中并且有尺寸
      if (mapRef.current.offsetWidth === 0 || mapRef.current.offsetHeight === 0) {
        setTimeout(() => initMap(), 100)
        return
      }

      try {
        const loader = new Loader({
          apiKey: (import.meta as any).env.VITE_GOOGLE_MAPS_API_KEY || '',
          version: 'weekly',
          libraries: ['places'],
        })

        const googleMaps = await loader.load()
        googleMapsRef.current = googleMaps

        if (mapRef.current) {
          const map = new googleMaps.maps.Map(mapRef.current, {
            center: { lat: 39.9042, lng: 116.4074 }, // 北京坐标作为默认中心点
            zoom: 10,
            mapTypeId: googleMaps.maps.MapTypeId.ROADMAP,
          })

          map.addListener('click', () => setSelectedPoint(null))

          mapInstanceRef.current = map
          setMapLoaded(true)
        }
      } catch (error) {
        console.error('Google Maps 加载失败:', error)
      }
    }

    initMap()
  }, [])

  // 获取位置数据。只取数不动筛选，「刷新」按钮复用它时要保住当前选中的国家和设备
  const fetchLocations = async (
    pathGroup: MapPathGroup,
    timeRange: string,
    date: string | undefined,
    withTestBuilds: boolean,
  ) => {
    setLoading(true)
    try {
      const params: Record<string, string> = date ? { date, pathGroup } : { timeRange, pathGroup }
      if (withTestBuilds) params.includeTestBuilds = '1'
      const response = await axios.get<APIResponse<MapLocationResponse>>(appApiUrls.locationMapLocations, {
        params,
      })

      if (response.data.code === 0) {
        const {
          locations: rows,
          userCount: users,
          totalCount: count,
          truncated: isTruncated,
          testBuildCount: testCount,
        } = response.data.data
        setLocations(rows)
        setUserCount(users)
        setTotalCount(count)
        setTruncated(isTruncated)
        setTestBuildCount(testCount ?? 0)
      } else {
        console.error('获取位置数据失败:', response.data.msg)
      }
    } catch (error) {
      console.error('请求位置数据失败:', error)
    } finally {
      setLoading(false)
    }
  }

  // 清除现有标记
  const clearMarkers = () => {
    setSelectedPoint(null)
    markersRef.current.forEach((marker) => marker.setMap(null))
    markersRef.current = []
    circlesRef.current.forEach((circle) => circle.setMap(null))
    circlesRef.current = []
  }

  // 在地图上显示位置标记
  const displayLocations = useCallback(() => {
    if (!mapInstanceRef.current || !mapLoaded || !googleMapsRef.current) return

    clearMarkers()

    if (mapPoints.length === 0) return

    const maps = googleMapsRef.current.maps
    const bounds = new maps.LatLngBounds()

    markersRef.current = mapPoints.map((point) => {
      const count = point.requests.length
      const marker = new maps.Marker({
        position: { lat: point.lat, lng: point.lng },
        map: mapInstanceRef.current,
        title: `${point.latest.actualLocation?.name || `位置 (${point.lat}, ${point.lng})`}${count > 1 ? ` · ${count} 次请求` : ''}`,
        icon: markerIcon(maps, point, false),
        label: count > 1 ? { text: String(count), color: '#ffffff', fontSize: '11px', fontWeight: '600' } : undefined,
        // 次数多的压在上面，几个点挨着时先看到大数
        zIndex: count,
      })
      marker.set('point', point)
      marker.addListener('click', () => setSelectedPoint(point))
      bounds.extend({ lat: point.lat, lng: point.lng })
      return marker
    })

    // Search tab: draw radius circles，同一处半径一样的只画一个
    if (mapSubTab === 'search') {
      circlesRef.current = mapPoints.flatMap((point) => {
        const color = point.latest.provider === 'amap' ? PROVIDER_COLORS.amap : PROVIDER_COLORS.gmap
        const radii = [...new Set(point.requests.map((location) => location.radius).filter(Boolean))]
        return radii.map(
          (radius) =>
            new maps.Circle({
              center: { lat: point.lat, lng: point.lng },
              radius,
              map: mapInstanceRef.current,
              fillColor: color,
              fillOpacity: 0.08,
              strokeColor: color,
              strokeOpacity: 0.3,
              strokeWeight: 1,
            }),
        )
      })
    }

    // 调整地图视图以包含所有标记
    mapInstanceRef.current.fitBounds(bounds)

    // 限制最大缩放级别：看全量时保持在多城市级别，避免缩进太深看不清位置；
    // 选了单台设备时它的点通常都在一个城市里，放到城区级别才分得开
    const maxZoom = selectedUser ? 11 : 8
    const listener = maps.event.addListenerOnce(mapInstanceRef.current, 'idle', () => {
      if (mapInstanceRef.current.getZoom() > maxZoom) {
        mapInstanceRef.current.setZoom(maxZoom)
      }
    })
    // 清理：如果组件卸载前事件未触发
    return () => googleMapsRef.current?.maps.event.removeListener(listener)
  }, [mapPoints, mapLoaded, mapSubTab, selectedUser])

  const reloadLocations = () =>
    fetchLocations(
      mapSubTab,
      selectedTimeRange,
      selectedDate ? selectedDate.format('YYYY-MM-DD') : undefined,
      includeTestBuilds,
    )

  // 时间范围、日期、子 tab 或测试设备开关改变时重新取数——服务端按 path 分组和构建渠道过滤，变了就得重取
  useEffect(() => {
    // 换 tab 时服务端返回的是另一个 path 分组，旧数据留着会让统计栏闪一下错数字
    setLocations([])
    setSelectedUser(null)
    setSelectedCountry(null)
    fetchLocations(
      mapSubTab,
      selectedTimeRange,
      selectedDate ? selectedDate.format('YYYY-MM-DD') : undefined,
      includeTestBuilds,
    )
  }, [mapSubTab, selectedTimeRange, selectedDate, includeTestBuilds])

  // 当筛选结果或地图加载状态改变时更新地图
  useEffect(() => {
    displayLocations()
  }, [mapPoints, mapLoaded, displayLocations])

  // 选中的点放大一圈，让人知道下面的详情说的是哪个
  useEffect(() => {
    if (!googleMapsRef.current) return
    markersRef.current.forEach((marker) => {
      const point: LocationPoint = marker.get('point')
      const isSelected = selectedPoint?.key === point.key
      marker.setIcon(markerIcon(googleMapsRef.current.maps, point, isSelected))
      marker.setZIndex(isSelected ? MAP_LOCATION_SAMPLE_LIMIT + 1 : point.requests.length)
    })
  }, [selectedPoint])

  // 手机上地图只有 300px 高，详情在地图下面，选中后把它带进视野
  useEffect(() => {
    if (selectedPoint) detailRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [selectedPoint])

  // 计算统计信息 (based on active sub-tab data)
  const validLocations = activeLocations.filter(
    (loc) => typeof loc.requestLat === 'number' && typeof loc.requestLon === 'number',
  )
  const successfulLocations = activeLocations.filter((loc) => loc.isSuccess)

  const amapLocations = activeLocations.filter((loc) => loc.provider === 'amap')
  const gmapLocations = activeLocations.filter((loc) => loc.provider === 'gmap')
  const amapSuccessful = amapLocations.filter((loc) => loc.isSuccess).length
  const gmapSuccessful = gmapLocations.filter((loc) => loc.isSuccess).length

  const stats = {
    total: activeLocations.length,
    valid: validLocations.length,
    successful: successfulLocations.length,
    successRate:
      activeLocations.length > 0 ? Number(((successfulLocations.length / activeLocations.length) * 100).toFixed(2)) : 0,
    amap: amapLocations.length,
    gmap: gmapLocations.length,
    amapSuccessful,
    gmapSuccessful,
    amapSuccessRate: amapLocations.length > 0 ? Number(((amapSuccessful / amapLocations.length) * 100).toFixed(2)) : 0,
    gmapSuccessRate: gmapLocations.length > 0 ? Number(((gmapSuccessful / gmapLocations.length) * 100).toFixed(2)) : 0,
    languages: {
      'zh-Hans': activeLocations.filter((loc) => loc.language === 'zh-Hans').length,
      en: activeLocations.filter((loc) => loc.language === 'en').length,
      'zh-Hant': activeLocations.filter((loc) => loc.language === 'zh-Hant').length,
    },
  }

  // Search tab specific stats
  const searchKeywordRanking = (() => {
    if (mapSubTab !== 'search') return []
    const keywordCounts: Record<string, number> = {}
    searchLocations.forEach((loc) => {
      if (loc.keyword) {
        keywordCounts[loc.keyword] = (keywordCounts[loc.keyword] || 0) + 1
      }
    })
    return Object.entries(keywordCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 20)
  })()

  const successfulSearchLocations = mapSubTab === 'search' ? searchLocations.filter((loc) => loc.isSuccess) : []
  const zeroResultCount =
    mapSubTab === 'search' ? successfulSearchLocations.filter((loc) => loc.resultCount === 0).length : 0
  const zeroResultRate =
    successfulSearchLocations.length > 0
      ? Number(((zeroResultCount / successfulSearchLocations.length) * 100).toFixed(2))
      : 0

  const timeRangeLabel = selectedDate
    ? selectedDate.format('YYYY-MM-DD')
    : TIME_RANGE_OPTIONS.find((o) => o.value === selectedTimeRange)?.label || '今天'
  // 列表标题里带上当前筛选，免得以为看的是全量
  const listFilterLabel = [
    timeRangeLabel,
    selectedCountry && countryLabel(selectedCountry),
    selectedUser && shortToken(selectedUser),
  ]
    .filter(Boolean)
    .join(' · ')
  const listedLocations = showFailedOnly ? failedLocations : filteredLocations

  // 每个国家除了请求数还数一遍去重设备数，看得出「请求多」是人多还是单机刷的
  const tallyByCountry = (rows: MapSearchLocationData[]) =>
    rows.reduce<Record<string, { count: number; devices: Set<string> }>>((acc, loc) => {
      const bucket = (acc[countryOf(loc)] ||= { count: 0, devices: new Set<string>() })
      bucket.count += 1
      if (loc.userToken) bucket.devices.add(loc.userToken)
      return acc
    }, {})

  // 国家 chips 的成员和顺序取自整个样本，选了设备这排也不会变短；
  // 数字跟着选中的设备走，这台设备没去过的国家显示 0 并灰掉
  const allCountryTally = tallyByCountry(activeLocations)
  const scopedCountryTally = selectedUser ? tallyByCountry(deviceFilteredLocations) : allCountryTally
  const countryDistribution = Object.entries(allCountryTally)
    .sort((a, b) => b[1].count - a[1].count)
    .map(([code]) => ({
      code,
      count: scopedCountryTally[code]?.count ?? 0,
      deviceCount: scopedCountryTally[code]?.devices.size ?? 0,
    }))

  // 设备 chips 只列选中国家里的设备，数字是它在这个国家的请求数。
  // chips 基于样本，这样每个 chip 点下去地图上一定有点；「共 N 台设备」用服务端全量聚合的 userCount，那才是真实值
  const userDistribution = Object.entries(
    countryFilteredLocations.reduce<Record<string, number>>((acc, loc) => {
      acc[deviceOf(loc)] = (acc[deviceOf(loc)] || 0) + 1
      return acc
    }, {}),
  ).sort((a, b) => b[1] - a[1])
  const collapsedUsers = showAllDevices ? userDistribution : userDistribution.slice(0, DEVICE_CHIP_LIMIT)
  // 选中的设备折叠在后面、或者在这个国家没有记录时，也要露出来，不然看不出当前筛着谁、也没处点掉
  const visibleUsers =
    selectedUser && !collapsedUsers.some(([token]) => token === selectedUser)
      ? [
          ...collapsedUsers,
          [selectedUser, userDistribution.find(([token]) => token === selectedUser)?.[1] ?? 0] as [string, number],
        ]
      : collapsedUsers

  const hasFilter = selectedCountry !== null || selectedUser !== null
  const filterResultLabel = (() => {
    if (filteredLocations.length === 0) {
      return selectedCountry && selectedUser
        ? `${shortToken(selectedUser)} 在${countryLabel(selectedCountry)}没有请求`
        : '没有请求'
    }
    const successCount = filteredLocations.length - failedLocations.length
    return [
      `${filteredLocations.length} 次请求，地图上 ${mapPoints.length} 处`,
      mapPoints.length < successCount && '同一处的请求合成一个点，数字是次数',
      failedLocations.length > 0 && `${failedLocations.length} 次失败不上图`,
    ]
      .filter(Boolean)
      .join('；')
  })()

  return (
    <div className={`flex flex-col gap-3 ${className}`}>
      {/* 控制栏：时间范围 + 子Tab + 加载状态 */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex items-center gap-1.5">
          {TIME_RANGE_OPTIONS.map((option) => (
            <button
              key={option.value}
              onClick={() => {
                setSelectedDate(null)
                setSelectedTimeRange(option.value)
              }}
              className={`rounded px-2.5 py-1 text-xs transition-colors ${
                selectedTimeRange === option.value && !selectedDate
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-slate-700 dark:text-gray-300 dark:hover:bg-slate-600'
              }`}
            >
              {option.label}
            </button>
          ))}
          <DatePicker
            value={selectedDate}
            onChange={(date) => setSelectedDate(date)}
            placeholder="选择日期"
            allowClear
            size="small"
            className="!w-28"
          />
        </div>
        <div className="flex border-b border-gray-200 dark:border-gray-600">
          <button
            onClick={() => handleSubTabChange('regeo')}
            className={`border-b-2 px-3 py-1.5 text-xs font-medium transition-colors ${
              mapSubTab === 'regeo'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            反向地理编码
          </button>
          <button
            onClick={() => handleSubTabChange('search')}
            className={`border-b-2 px-3 py-1.5 text-xs font-medium transition-colors ${
              mapSubTab === 'search'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            地点搜索
          </button>
        </div>
        <label
          className="flex cursor-pointer items-center gap-1 text-xs text-gray-500 dark:text-gray-400"
          title="模拟器、调试包、TestFlight 发出的请求。客户端从 1.28.1 起才带构建渠道，更早的请求都算正式版"
        >
          <input
            type="checkbox"
            checked={includeTestBuilds}
            onChange={(event) => setIncludeTestBuilds(event.target.checked)}
          />
          含测试设备{testBuildCount > 0 && <span className="text-gray-400">（{testBuildCount} 条）</span>}
        </label>
        {loading && <span className="text-xs text-blue-500">加载中...</span>}
      </div>

      {/* 紧凑统计栏 */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 rounded border bg-gray-50 px-4 py-2 text-xs dark:border-gray-600 dark:bg-slate-700">
        <span className="dark:text-white">
          <span className="text-gray-500 dark:text-gray-400">总计</span>{' '}
          <strong className="text-base">{totalCount}</strong>
        </span>
        {truncated && (
          <span
            className="text-amber-600 dark:text-amber-400"
            title={`地图和下面的分项统计只取了最近 ${MAP_LOCATION_SAMPLE_LIMIT} 条，总计和设备分布仍是全量`}
          >
            分项基于最近 {stats.total} 条
          </span>
        )}
        <span className="text-red-600 dark:text-red-400">
          高德 {stats.amap} <span className="text-gray-400">({stats.amapSuccessRate}%)</span>
        </span>
        <span className="text-blue-600 dark:text-blue-400">
          Google {stats.gmap} <span className="text-gray-400">({stats.gmapSuccessRate}%)</span>
        </span>
        <span className="text-green-600 dark:text-green-400">
          成功率 {stats.successRate}%{' '}
          <span className="text-gray-400">
            ({stats.successful}/{stats.total})
          </span>
        </span>
        <span className="text-gray-500 dark:text-gray-400">
          中 {stats.languages['zh-Hans']} · 英 {stats.languages.en} · 繁 {stats.languages['zh-Hant']}
        </span>
      </div>

      {/* 国家或地区（单选）。码取自返回结果；高德写死 CN，香港澳门由服务端按省份名拆出 */}
      {countryDistribution.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 text-xs">
          <span
            className="text-gray-500 dark:text-gray-400"
            title="按请求落点所在国家或地区筛选，和设备是 AND；选了设备时数字是这台设备在各地的请求数"
          >
            国家或地区 {countryDistribution.length}
            {selectedUser ? (
              <span className="text-gray-400 dark:text-gray-500">（{shortToken(selectedUser)} 的请求）</span>
            ) : (
              <span className="text-gray-400 dark:text-gray-500">（请求/设备）</span>
            )}
            :
          </span>
          <FilterChip active={selectedCountry === null} onClick={() => setSelectedCountry(null)}>
            <span className="text-gray-700 dark:text-gray-300">全部</span>
          </FilterChip>
          {countryDistribution.map(({ code, count, deviceCount }) => (
            <FilterChip
              key={code}
              active={selectedCountry === code}
              muted={count === 0}
              onClick={() => setSelectedCountry(selectedCountry === code ? null : code)}
              title={`${count} 次请求 · ${deviceCount} 台设备`}
            >
              <span className="text-gray-700 dark:text-gray-300">{countryLabel(code)}</span>
              <span className="font-semibold text-blue-600 dark:text-blue-400">{count}</span>
              {!selectedUser && (
                <>
                  <span className="text-gray-400 dark:text-gray-500">/</span>
                  <span className="font-semibold text-emerald-600 dark:text-emerald-400">{deviceCount}台</span>
                </>
              )}
            </FilterChip>
          ))}
        </div>
      )}

      {/* 设备（单选）。数的是发过定位请求的设备，含 iOS 后台唤醒，不等于日活 */}
      {userCount > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 text-xs">
          <span className="text-gray-500 dark:text-gray-400" title="发过定位请求的设备数（含后台唤醒），不是日活用户数">
            {selectedCountry
              ? `设备 ${userDistribution.length}（${countryLabel(selectedCountry)}的请求）`
              : `设备 ${userCount}${truncated ? `（样本内 ${userDistribution.length} 台）` : ''}`}
            :
          </span>
          <FilterChip active={selectedUser === null} onClick={() => setSelectedUser(null)}>
            <span className="text-gray-700 dark:text-gray-300">全部</span>
          </FilterChip>
          {visibleUsers.map(([token, count]) => (
            <FilterChip
              key={token}
              active={selectedUser === token}
              onClick={() => setSelectedUser(selectedUser === token ? null : token)}
              title={token}
            >
              <span className="max-w-[80px] truncate text-gray-700 dark:text-gray-300">{shortToken(token)}</span>
              <span className="font-semibold text-blue-600 dark:text-blue-400">{count}</span>
            </FilterChip>
          ))}
          {userDistribution.length > DEVICE_CHIP_LIMIT && (
            <button
              type="button"
              onClick={() => setShowAllDevices(!showAllDevices)}
              className="px-1 text-blue-500 hover:text-blue-700"
            >
              {showAllDevices ? '收起' : `展开全部 ${userDistribution.length} 台`}
            </button>
          )}
        </div>
      )}

      {/* 当前筛选和结果：两个条件是 AND，在这里写明白，也顺带解释地图上的点为什么比请求少 */}
      {activeLocations.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded border border-dashed border-gray-200 px-2 py-1 text-xs dark:border-gray-600">
          {hasFilter ? (
            <>
              {selectedCountry && (
                <button
                  type="button"
                  onClick={() => setSelectedCountry(null)}
                  className="inline-flex items-center gap-0.5 rounded bg-blue-100 px-1.5 py-0.5 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
                >
                  {countryLabel(selectedCountry)}
                  <XMarkIcon className="h-3 w-3" />
                </button>
              )}
              {selectedCountry && selectedUser && <span className="text-gray-400">且</span>}
              {selectedUser && (
                <button
                  type="button"
                  onClick={() => setSelectedUser(null)}
                  className="inline-flex items-center gap-0.5 rounded bg-blue-100 px-1.5 py-0.5 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
                >
                  {shortToken(selectedUser)}
                  <XMarkIcon className="h-3 w-3" />
                </button>
              )}
            </>
          ) : (
            <span className="text-gray-500 dark:text-gray-400">全部国家或地区和设备</span>
          )}
          <span
            className={
              filteredLocations.length === 0 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-600 dark:text-gray-300'
            }
          >
            → {filterResultLabel}
          </span>
          {hasFilter && (
            <button
              type="button"
              onClick={() => {
                setSelectedCountry(null)
                setSelectedUser(null)
              }}
              className="ml-auto text-blue-500 hover:text-blue-700"
            >
              清除筛选
            </button>
          )}
        </div>
      )}

      {/* 地图（更高） */}
      <div className="overflow-hidden rounded-lg border border-gray-300 dark:border-gray-600">
        <div ref={mapRef} className="h-[300px] w-full md:h-[400px] lg:h-[480px]">
          {/* Google Maps 将在这里渲染 */}
        </div>
      </div>

      {/* 标记详情：放在地图下方而不是气泡里，手机上不用在小气泡里滚 */}
      <div ref={detailRef}>
        {selectedPoint ? (
          <div className="rounded-lg border border-blue-300 bg-white p-3 dark:border-blue-500/60 dark:bg-slate-700">
            <div className="mb-2 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h4 className="text-sm font-medium text-gray-800 dark:text-slate-100">
                  {selectedPoint.latest.actualLocation?.name || '未知位置'}
                </h4>
                {selectedPoint.requests.length > 1 && (
                  <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                    此处 {selectedPoint.requests.length} 次请求 · {new Set(selectedPoint.requests.map(deviceOf)).size}{' '}
                    台设备 · {formatTime(selectedPoint.earliest.createdAt)} 到{' '}
                    {formatTime(selectedPoint.latest.createdAt)}
                    {mapSubTab === 'search' &&
                      ` · 关键字：${[...new Set(selectedPoint.requests.map((location) => location.keyword).filter(Boolean))].slice(0, 10).join('、')}`}
                    。下面是最近一次
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setSelectedPoint(null)}
                className="shrink-0 rounded px-2 py-0.5 text-xs text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-slate-600"
              >
                收起
              </button>
            </div>
            <dl className="grid grid-cols-1 gap-x-6 gap-y-1.5 text-xs sm:grid-cols-2 lg:grid-cols-3">
              {detailRows(selectedPoint.latest, mapSubTab).map(({ label, value }) => (
                <div
                  key={label}
                  className="flex justify-between gap-2 border-b border-dashed border-gray-100 pb-1 dark:border-gray-600"
                >
                  <dt className="shrink-0 text-gray-500 dark:text-gray-400">{label}</dt>
                  <dd className="truncate text-right font-medium text-gray-800 dark:text-slate-100" title={value}>
                    {value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        ) : (
          <p className="px-1 text-xs text-gray-400 dark:text-gray-500">点击地图上的标记查看详情</p>
        )}
      </div>

      {/* 分析面板 + 最近请求 并排 */}
      <div className={`grid gap-3 ${mapSubTab === 'search' ? 'grid-cols-1 lg:grid-cols-3' : 'grid-cols-1'}`}>
        {/* Search tab: 关键字排行 + 搜索结果分析 */}
        {mapSubTab === 'search' && (
          <div className="rounded border bg-white p-3 dark:border-gray-600 dark:bg-slate-700 lg:col-span-1">
            <h4 className="mb-2 text-sm font-medium dark:text-white">搜索分析</h4>
            <div className="mb-3 space-y-1.5 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">零结果次数</span>
                <span className="font-medium text-red-600 dark:text-red-400">{zeroResultCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">零结果率</span>
                <span className="font-medium text-red-600 dark:text-red-400">{zeroResultRate}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">平均结果数</span>
                <span className="font-medium dark:text-white">
                  {(() => {
                    const s = searchLocations.filter((loc) => loc.isSuccess && loc.resultCount !== undefined)
                    if (s.length === 0) return 'N/A'
                    return (s.reduce((sum, loc) => sum + (loc.resultCount || 0), 0) / s.length).toFixed(1)
                  })()}
                </span>
              </div>
            </div>
            <h4 className="mb-2 text-sm font-medium dark:text-white">关键字 Top 20</h4>
            {searchKeywordRanking.length === 0 ? (
              <div className="text-xs text-gray-400">暂无数据</div>
            ) : (
              <div className="max-h-48 space-y-0.5 overflow-y-auto">
                {searchKeywordRanking.map(([keyword, count], index) => (
                  <div key={keyword} className="flex items-center gap-1.5 text-xs">
                    <span className="w-5 text-right text-gray-400">{index + 1}</span>
                    <span className="flex-1 truncate dark:text-white">{keyword}</span>
                    <span className="font-medium text-blue-600 dark:text-blue-400">{count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 最近请求 + 失败列表合并 */}
        <div
          className={`rounded border bg-white dark:border-gray-600 dark:bg-slate-700 ${mapSubTab === 'search' ? 'lg:col-span-2' : ''}`}
        >
          <div className="flex items-center justify-between border-b px-3 py-1.5 text-xs dark:border-gray-600">
            <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
              <button
                onClick={() => setShowFailedOnly(false)}
                className={`font-medium ${showFailedOnly ? 'text-gray-400 hover:text-gray-600' : 'dark:text-white'}`}
              >
                最近请求 {filteredLocations.length}
              </button>
              <span className="text-gray-400">·</span>
              <button
                onClick={() => setShowFailedOnly(true)}
                className={`font-medium ${showFailedOnly ? 'dark:text-white' : 'text-gray-400 hover:text-gray-600'}`}
              >
                失败列表 <span className="text-red-500">{failedLocations.length}</span>
              </button>
              <span className="text-gray-400">({listFilterLabel})</span>
              <button
                onClick={reloadLocations}
                disabled={loading}
                className="text-blue-500 hover:text-blue-700 disabled:text-gray-400"
              >
                {loading ? '刷新中' : '刷新'}
              </button>
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto">
            {listedLocations.length === 0 ? (
              <div className="px-3 py-2 text-xs text-gray-400">{showFailedOnly ? '没有失败记录' : '暂无数据'}</div>
            ) : (
              <>
                {listedLocations.slice(0, 200).map((location, index) => (
                  <div
                    key={location.id || index}
                    className={`flex items-center justify-between border-b px-3 py-1.5 text-xs last:border-b-0 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-slate-600 ${
                      !location.isSuccess ? 'bg-red-50 dark:bg-red-900/20' : ''
                    }`}
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      <span
                        className={`shrink-0 rounded px-1 py-0.5 ${
                          location.path === '/search-text'
                            ? 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400'
                            : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                        }`}
                      >
                        {location.path === '/search-text' ? '搜索' : '逆编码'}
                      </span>
                      <span className="truncate font-medium dark:text-white" style={{ maxWidth: '200px' }}>
                        {mapSubTab === 'search' && location.keyword
                          ? `${location.keyword}${location.resultCount !== undefined ? ` (${location.resultCount})` : ''}`
                          : location.isSuccess
                            ? location.actualLocation?.name || '未知位置'
                            : '请求失败'}
                      </span>
                      {!location.isSuccess && (
                        <span className="rounded bg-red-200 px-1.5 py-0.5 text-red-800 dark:bg-red-800 dark:text-red-200">
                          {location.responseCode}
                        </span>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-2 font-mono text-gray-400 dark:text-gray-500">
                      <span title={`${location.requestLat}, ${location.requestLon}`}>
                        {location.requestLat?.toFixed(2)},{location.requestLon?.toFixed(2)}
                      </span>
                      <span
                        className={`rounded px-1.5 py-0.5 ${
                          location.provider === 'amap'
                            ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                            : 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                        }`}
                      >
                        {location.provider === 'amap' ? '高德' : 'G'}
                      </span>
                      <span className="w-14 text-right">
                        {location.createdAt
                          ? new Date(location.createdAt).toLocaleTimeString('zh-CN', {
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : ''}
                      </span>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
